import type { ChatMessage as ChatMessageType } from "schemas/generated/chatMessage";

import React, { useState } from "react";

import { Button } from "@replo/design-system/components/button/Button";
import {
  Menu,
  MenuContent,
  MenuItem,
  MenuTrigger,
} from "@replo/design-system/components/menu/Menu";
import { toast } from "@replo/design-system/components/toast/Toast";
import { Tooltip } from "@replo/design-system/components/tooltip/Tooltip";
import { CircleCheckIcon } from "@replo/design-system/icons/animated/CircleCheckIcon";
import { CopyIcon } from "@replo/design-system/icons/animated/CopyIcon";
import { DownvoteIcon } from "@replo/design-system/icons/animated/DownvoteIcon";
import { UpvoteIcon } from "@replo/design-system/icons/animated/UpvoteIcon";
import twMerge from "@replo/design-system/utils/twMerge";
import { useErrorToast } from "@v2/hooks/useErrorToast";
import { useLogAnalytics } from "@v2/hooks/useLogAnalytics";
import { chatRouter, chatUtils } from "@v2/trpc/routes/chat";
import { writeHtmlToClipboard } from "@v2/utils/clipboard";
import shouldShowV2Debuggers from "@v2/utils/shouldShowV2Debuggers";
import { ExternalLink, MoreHorizontal } from "lucide-react";

import { useChatId } from "../../hooks/useChatId";
import { markdownToHtml } from "../../utils/markdownToHtml";

interface ChatActionsProps {
  messages: ChatMessageType[];
  role: "user" | "assistant";
  isChatLoading?: boolean;
  isLastAssistantGroup?: boolean;
}

const THINK_TAG_REGEX = /<think>([\S\s]*?)<\/think>/g;

type TextContentItem = Extract<
  ChatMessageType["content"][number],
  {
    type: "text";
  }
>;

const buildClipboardPayload = (messages: ChatMessageType[]) => {
  const htmlParts: string[] = [];

  for (const message of messages) {
    const textItems = message.content.filter(
      (item): item is TextContentItem => item.type === "text",
    );

    for (const item of textItems) {
      const cleaned = item.text.replace(THINK_TAG_REGEX, "").trim();
      if (!cleaned) {
        continue;
      }

      const html = markdownToHtml(cleaned);
      htmlParts.push(html);
    }
  }

  return {
    html: htmlParts.join(""),
  };
};

export const ChatActions: React.FC<ChatActionsProps> = ({
  messages,
  role,
  isChatLoading = false,
  isLastAssistantGroup = false,
}) => {
  const chatId = useChatId();
  const logEvent = useLogAnalytics();
  const errorToast = useErrorToast();

  const [isCopied, setIsCopied] = useState(false);
  const [optimisticFeedback, setOptimisticFeedback] = useState<
    "positive" | "negative" | "none" | null
  >(null);

  const actualFeedback =
    messages.find((message) => message.feedback !== null)?.feedback ?? null;

  const currentFeedback = optimisticFeedback ?? actualFeedback;

  const { mutate: submitFeedback } = chatRouter.submitFeedback.useMutation({
    onSuccess: () => {
      if (chatId) {
        void chatUtils.getPaginatedMessages.invalidate({
          chatId,
        });
      }
    },
    onError: () => {
      setOptimisticFeedback(null);
      toast.error({
        title: "Failed to submit feedback",
        description: "Please try again.",
      });
    },
  });

  if (isLastAssistantGroup && isChatLoading) {
    return null;
  }

  const extractLLMRequestId = (messages: ChatMessageType[]): string | null => {
    for (const message of messages) {
      for (const contentItem of message.content) {
        if (contentItem.llmRequestId) {
          return contentItem.llmRequestId;
        }
      }
    }
    return null;
  };

  const handleCopy = async () => {
    try {
      const { html } = buildClipboardPayload(messages);

      if (!html) {
        return;
      }

      await writeHtmlToClipboard(html, errorToast);

      setIsCopied(true);
      toast({ title: "Message copied to clipboard" });

      setTimeout(() => {
        setIsCopied(false);
      }, 2000);
    } catch (error) {
      console.error("Failed to copy message:", error);
    }
  };

  const handleFeedback = (rating: "positive" | "negative" | "none") => {
    logEvent("chat.feedback", {
      rating,
      chatId: chatId ?? "",
    });

    const llmRequestId = extractLLMRequestId(messages);
    if (!llmRequestId || !chatId) {
      return;
    }

    const actualCurrentFeedback =
      messages.find((message) => message.feedback !== null)?.feedback ?? null;
    const newRating = actualCurrentFeedback === rating ? "none" : rating;

    setOptimisticFeedback(newRating);

    submitFeedback({
      chatId,
      llmRequestId,
      rating: newRating,
      messageIds: messages.map((message) => message.id),
    });
  };

  const isDebug = shouldShowV2Debuggers();

  // Get the first message for admin links (typically the first message in the group)
  const firstMessage = messages[0];
  const firstMessageId = firstMessage?.id;
  const firstChatTurnId = firstMessage?.chatTurnId;

  const messageAdminUrl = firstMessageId
    ? `https://admin.replo.app/dashboard/chat-messages?chatMessageId=${firstMessageId}`
    : null;
  const chatTurnAdminUrl = firstChatTurnId
    ? `https://admin.replo.app/dashboard/chat-turns?chatTurnId=${firstChatTurnId}`
    : null;

  const hasDebugMenuItems =
    isDebug && (Boolean(messageAdminUrl) || Boolean(chatTurnAdminUrl));

  return (
    <div className="flex flex-row gap-1 transition-opacity">
      {role === "assistant" && (
        <>
          <Tooltip content="Good response">
            <Button
              icon
              variant="tertiary"
              size="sm"
              onClick={() => handleFeedback("positive")}
            >
              <UpvoteIcon
                className={twMerge(
                  "stroke-current",
                  currentFeedback === "positive"
                    ? "text-emerald"
                    : "text-muted",
                )}
              />
            </Button>
          </Tooltip>
          <Tooltip content="Poor response">
            <Button
              icon
              variant="tertiary"
              size="sm"
              onClick={() => handleFeedback("negative")}
            >
              <DownvoteIcon
                className={twMerge(
                  "stroke-current",
                  currentFeedback === "negative" ? "text-rose" : "text-muted",
                )}
              />
            </Button>
          </Tooltip>
        </>
      )}
      <Tooltip content="Copy message">
        <Button
          icon
          variant="tertiary"
          size="sm"
          onClick={() => void handleCopy()}
        >
          {isCopied ? (
            <CircleCheckIcon className="text-emerald" />
          ) : (
            <CopyIcon className="text-muted" />
          )}
        </Button>
      </Tooltip>
      {hasDebugMenuItems && (
        <Menu>
          <Tooltip content="More options" triggerAsChild>
            <MenuTrigger
              render={
                <Button icon variant="tertiary" size="sm">
                  <MoreHorizontal />
                </Button>
              }
            />
          </Tooltip>
          <MenuContent align="end">
            {messageAdminUrl && (
              <MenuItem
                label="View Message In Admin"
                startEnhancer={<ExternalLink />}
                onClick={() => {
                  window.open(messageAdminUrl, "_blank");
                }}
              />
            )}
            {chatTurnAdminUrl && (
              <MenuItem
                label="View Chat Turn In Admin"
                startEnhancer={<ExternalLink />}
                onClick={() => {
                  window.open(chatTurnAdminUrl, "_blank");
                }}
              />
            )}
          </MenuContent>
        </Menu>
      )}
    </div>
  );
};
